// DECKING CHOICES — re-pricing the Add Items for a different Trex tier.
//
// The Decking Tier Pricing panel (and the proposal's Decking Choices block)
// asks "what would this whole job cost in Trex <tier>?". Every add item whose
// price is built off the decking board has to move with the tier; anything
// else keeps its current price.
//
// Moved out of App.tsx's trexLevelsWhatIf so it can be tested on its own.
// Behaviour is unchanged except for construction-type rows (below).
//
// CONSTRUCTION-TYPE ROWS (e.g. "New Construction – Trex Enhance Naturals" added
// as an Add Item on a resurface job) carry their OWN decking pick and are
// priced at that board's $/sf plus the construction adjustment. Their name and
// category contain none of the deck keywords, so they used to fall through to
// "keep the current price" — every tier showed those square feet at the
// quoted board's price. Now, when the row's board is a Trex board, it is
// re-priced at the tier's $/sf with the same construction adjustment.

import { isConstructionTypeCategory } from "./pricing";

type AnyRow = Record<string, any>;

export function altAddItemsSubtotalUpliftable(args: {
  addItemsDetailed: AnyRow[];
  pricingItems: AnyRow[];
  /** $/sf of the decking currently selected in the main Decking section. */
  curDeckSf: number;
  /** $/sf of the Trex tier being priced. */
  altDeckSf: number;
}): number {
  const { addItemsDetailed, pricingItems, curDeckSf, altDeckSf } = args;

  return (addItemsDetailed || [])
    .filter(
      (r) =>
        r &&
        r.picked &&
        !r.isFixedPrice &&
        !r.optional &&
        Number(r.lineBase || 0) !== 0
    )
    .reduce((sum, r) => {
      const qty = Number(r.qty || 0);
      if (qty <= 0) return sum;

      const keep = sum + (Number(r.lineBase) || 0);

      // Construction-type row: priced off its own decking pick.
      if (isConstructionTypeCategory(String(r.category || ""))) {
        const lineDeck = (pricingItems || []).find(
          (d) => String(d?.id) === String(r.deckingId || "")
        );
        const lineDeckSf = Number(lineDeck?.cost || 0);
        const lineDeckName = String(lineDeck?.name || "").toLowerCase();
        if (!(lineDeckSf > 0 && altDeckSf > 0) || !lineDeckName.includes("trex"))
          return keep;
        // unitCost = line board $/sf + construction adjustment; keep the
        // adjustment, swap the board.
        const adjSf = (Number(r.unitCost) || 0) - lineDeckSf;
        const altUnit = altDeckSf + adjSf;
        return altUnit > 0 ? sum + altUnit * qty : keep;
      }

      const picked = r.picked || {};
      const pickedUnit = String(picked.unit || r.unit || r.unitLabel || "")
        .toLowerCase()
        .trim();

      const nameLc = String(r.displayName || picked.name || "").toLowerCase();
      const catLc = String(r.category || "").toLowerCase();

      const isPicFrame =
        nameLc.includes("pic frame") ||
        nameLc.includes("picture frame") ||
        nameLc.includes("picframe");

      const isPlanter = nameLc.includes("planter");
      const isRamp = nameLc.includes("ramp");

      const referencesDecking =
        catLc.includes("deck") ||
        catLc === "decking_options" ||
        nameLc.includes("deck") ||
        nameLc.includes("stair landing") ||
        nameLc.includes("diagonal") ||
        nameLc.includes("picture frame") ||
        nameLc.includes("pic frame") ||
        nameLc.includes("planter") ||
        nameLc.includes("ramp") ||
        nameLc.includes("board over board");

      // if this add-item doesn't depend on decking, keep its existing lineBase
      if (!referencesDecking) return keep;

      // If we don't have deck $/sf data, fall back safely to existing lineBase
      if (!(curDeckSf > 0 && altDeckSf > 0)) return keep;

      const pickedCost = Number(picked.cost || 0);

      // Pic Frame Border: 0.75 × decking $/sf
      if (isPicFrame) return sum + altDeckSf * 0.75 * qty;

      // Planter: 0.90 × decking $/sf
      if (isPlanter) return sum + altDeckSf * 0.9 * qty;

      // Ramp: (picked multiplier) × decking $/sf
      if (isRamp) return sum + altDeckSf * pickedCost * qty;

      // multiplier: base × mult
      if (pickedUnit === "multiplier") return sum + altDeckSf * pickedCost * qty;

      // addon_sf / addon_lf: base + add
      if (pickedUnit === "addon_sf" || pickedUnit === "addon_lf")
        return sum + (altDeckSf + pickedCost) * qty;

      // Default deck-based fallback: scale linearly with decking cost
      return sum + (Number(r.lineBase) || 0) * (altDeckSf / curDeckSf);
    }, 0);
}

// SKIRTING for a different Trex tier.
//
// On the Estimate page, picking a decking auto-selects the skirting row whose
// name contains the decking name (App.tsx skirting auto-match effect) unless
// the skirting was picked by hand. So "this job in Trex Select" carries Trex
// Select skirting, not the quoted Trex Enhance Naturals skirting. The tier
// math used to keep the quoted skirting row, leaving every tier short by the
// skirting price difference (Great Neck Forst 11/5: $2.50/sf × 173 sf).
//
// Rule: if the job's skirting row is the one matched to the current decking
// (its name contains the decking name), use the skirting row matched to the
// tier. Anything else — a hand-picked or unrelated skirting — is kept.
export function tierSkirtingRow<T extends AnyRow>(args: {
  pricingItems: T[];
  selectedSkirting: T | null | undefined;
  currentDeckName: string;
  tierDeckName: string;
}): T | undefined {
  const { pricingItems, selectedSkirting, currentDeckName, tierDeckName } = args;
  if (!selectedSkirting) return undefined;
  const cur = String(currentDeckName || "").trim().toLowerCase();
  const tier = String(tierDeckName || "").trim().toLowerCase();
  const skirtName = String(selectedSkirting.name || "").trim().toLowerCase();
  if (!cur || !tier || !skirtName.includes(cur)) return selectedSkirting;
  const match = (pricingItems || []).find(
    (p) =>
      p &&
      p.active !== false &&
      (p.deleted_at ?? null) == null &&
      String(p.category || "").toLowerCase().includes("skirting") &&
      String(p.name || "").trim().toLowerCase().includes(tier)
  );
  return match || selectedSkirting;
}
